import { useState, useCallback, useMemo, useEffect } from "react";
import Select from "components/ui/Select";
import { useSearchItemsForAsyncSelect } from "http-hooks/agitem/useAgitem";

const AsyncItemSelect = ({ 
  value = null, 
  onChange, 
  vendorId = null, 
  label,
  placeholder = "Search and select item...",
  isClearable = true,
  isDisabled = false,
  className = "",
  ...props 
}) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const shouldSearch = debouncedInputValue.trim().length >= 3;

  const { data: searchResults = [], isLoading } = useSearchItemsForAsyncSelect(
    { 
      query: shouldSearch ? debouncedInputValue : "", 
      vendorId 
    },
    {
      enabled: shouldSearch,
    }
  );

  const options = useMemo(() => {
    return searchResults.map(item => ({
      value: item.id,
      label: item.name,
      itemuom: item.itemuom,
      itmbasicuom: item.itmbasicuom,
      conval: item.conval,
      packratio: item.packratio,
      itmsztyp: item.itmsztyp,
      itmhsnId: item.itmhsnId
    }));
  }, [searchResults]);

  const handleInputChange = useCallback((val) => {
    setInputValue(val);
  }, []);

  const handleSelectionChange = useCallback((selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }
    setInputValue("");
  }, [onChange]);

  const noOptionsMsg = useCallback(() => {
    if (inputValue.trim().length < 3) return "Type at least 3 characters to search...";
    if (isLoading) return "Searching...";
    return "No items found";
  }, [inputValue, isLoading]);

  return (
    <Select
      {...props}
      label={label}
      value={value}
      options={options}
      onChange={handleSelectionChange}
      onInputChange={handleInputChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading && shouldSearch}
      className={className}
      filterOption={false}
      noOptionsMessage={noOptionsMsg}
    />
  );
};

export default AsyncItemSelect;
